import { defineStore } from 'pinia';
import API from '@/shared/helpers/API.js';
import {useEpsonStore, useUserStore} from "@/console-new/stores";
import {getCompatibleDate, getCompatibleDateOnly, getLocalDateFromServer, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {useLanguageStore, useNotificationStore, useCurrencyStore} from "@/shared/stores";
import {capitalizeFirstLetter} from "@/shared/helpers/strings";

export const useAnalyticsStore = defineStore('analytics', {
	state: () => ({
		//data needs to be separated for each section otherwise the charts animation will be buggy
		//also it can differ slightly from the data in the other sections
		ordersToday: [],
		ordersAggregated: {
			full: [],
			type: [],
			mode: [],
			staff: [],
			payment_user: [],
			timeGrain: null,
			bucketColumn: null,
			users: []
		},
		ordersAggregatedToday: {
			full: [],
			timeGrain: null,
			bucketColumn: null
		},
		viewsAggregated: {
			events: [],
			visits: [],
			visits_browser: [],
			visits_menu: [],
			visits_room: [],
			translations: [],
			timeGrain: null,
			bucketColumn: null,
			menus: [],
			rooms: []
		},
		viewsAggregatedToday: {
			events: [],
			visits_menu: [],
			timeGrain: null,
			bucketColumn: null
		},
		productsOrdersToday: [],
		productsEventsToday: [],
		productsAggregated: {
			products: [],
			categories: [],
			variations: [],
			vat: [],
			products_metadata: [],
			categories_metadata: [],
			variations_metadata: [],
			timeGrain: null,
			bucketColumn: null
		},
		productsAggregatedToday: {
			products: [],
			categories: [],
			variations: [],
			vat: [],
			products_metadata: [],
			categories_metadata: [],
			variations_metadata: [],
			timeGrain: null,
			bucketColumn: null
		},
		tablesOrdersToday: [],
		tablesViewsToday: [],
		tablesAggregated: {
			tables_orders: [],
			rooms_orders: [],
			tables_visits: [],
			rooms_visits: [],
			tables_metadata: [],
			rooms_metadata: [],
			timeGrain: null,
			bucketColumn: null
		},
		tablesAggregatedToday: {
			tables_orders: [],
			rooms_orders: [],
			tables_visits: [],
			rooms_visits: [],
			tables_metadata: [],
			rooms_metadata: [],
			timeGrain: null,
			bucketColumn: null
		},
		heatMap: [],
		heatMapTimeGrain: null,
		weather: [],
		bestColors: ["#2CECBE", "#03c6f3", "#0752A3",
			"#FDDE00", "#e57e00", "#FC2291"],
		activeMenus: null,
		loadedViews: false,
		loadedOrders: false,
		loadedProducts: false,
		loadedTables: false,
		// Reviews bottom sheet state
		reviewsBottomSheetActive: false,
		ordersReviews: [],
		ordersReviewsPagination: { page: 1, hasMore: true, total: 0 },
	}),
	actions: {
		getDayShiftDatetime(referenceDate = null) {
			const userStore = useUserStore();
			const dayShift = userStore.currBusiness?.day_shift || '03:00:00';
			const splitShift = dayShift.split(':');
			const shiftHour = parseInt(splitShift[0]) || 3;
			const shiftMinute = parseInt(splitShift[1]) || 0;
			const shiftSecond = parseInt(splitShift[2]) || 0;

			// Use provided reference date or current time
			const refDate = referenceDate ? new Date(referenceDate) : new Date();

			// Create day shift time for the reference date
			const dayShiftTime = new Date(refDate);
			dayShiftTime.setHours(shiftHour, shiftMinute, shiftSecond, 0);

			// If reference time is before the day shift time on the same day,
			// the actual day shift start is the previous day
			if (refDate < dayShiftTime) {
				dayShiftTime.setDate(dayShiftTime.getDate() - 1);
			}

			return dayShiftTime;
		},
		getPreviousDayShiftDatetime(referenceDate = null) {
			// Get the day shift for the reference date
			const currentDayShift = this.getDayShiftDatetime(referenceDate);
			// Subtract 24 hours to get the previous day's shift start
			const previousDayShift = new Date(currentDayShift);
			previousDayShift.setDate(previousDayShift.getDate() - 1);
			return previousDayShift;
		},
		getStatsViewsAggregated(startDate, endDate, callback = null, isToday = false) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsViewsAggregated(userStore.currBusinessId, startDate, endDate, response => {
					if (response && response.result === "OK") {
						(async () => {
							if (isToday) {
								// Store only the essential data for 24-hour view
								this.viewsAggregatedToday = {
									events: this.normalizeAggregatedData(response.aggregated_data.events || [], response.bucket_column),
									visits_menu: this.normalizeAggregatedData(response.aggregated_data.visits_menu || [], response.bucket_column),
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							} else {
								// Store the aggregated data with all dimensions
								this.viewsAggregated = {
									events: this.normalizeAggregatedData(response.aggregated_data.events || [], response.bucket_column),
									visits: this.normalizeAggregatedData(response.aggregated_data.visits || [], response.bucket_column),
									visits_browser: this.normalizeAggregatedData(response.aggregated_data.visits_browser || [], response.bucket_column),
									visits_menu: this.normalizeAggregatedData(response.aggregated_data.visits_menu || [], response.bucket_column),
									visits_room: this.normalizeAggregatedData(response.aggregated_data.visits_room || [], response.bucket_column),
									translations: this.normalizeAggregatedData(response.aggregated_data.translations || [], response.bucket_column),
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column,
									menus: response.menus || [],
									rooms: response.rooms || []
								};
							}
							callback && callback(response);
						})()
					}
				});
			}
		},
		getStatsHeatMapAggregated(startDate, endDate, callback = null) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsHeatMapAggregated(userStore.currBusinessId, startDate, endDate, response => {
					if (response && response.result === "OK") {
						(async () => {
							this.heatMap = response.map;
							this.heatMapTimeGrain = response.time_grain;
							callback && callback(response);
						})()
					}
				});
			}
		},
		getStatsOrdersAggregated(startDate, endDate, callback = null, isToday = false) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsOrdersAggregated(userStore.currBusinessId, startDate, endDate, userStore.manualCode, response => {
					if (response && response.result === "OK") {
						(async () => {
							if (isToday) {
								// Store only the full dimension data for 24-hour view
								this.ordersAggregatedToday = {
									full: this.normalizeAggregatedData(response.aggregated_data.full || [], response.bucket_column, startDate),
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							} else {
								// Store the aggregated data for all dimensions
								this.ordersAggregated = {
									full: this.normalizeAggregatedData(response.aggregated_data.full || [], response.bucket_column, startDate),
									type: this.normalizeAggregatedData(response.aggregated_data.type || [], response.bucket_column, startDate),
									mode: this.normalizeAggregatedData(response.aggregated_data.mode || [], response.bucket_column, startDate),
									staff: this.normalizeAggregatedData(response.aggregated_data.staff || [], response.bucket_column, startDate),
									payment_user: this.normalizeAggregatedData(response.aggregated_data.payment_user || [], response.bucket_column, startDate),
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column,
									users: response.users || []
								};
							}
							callback && callback(response);
						})()
					}
					else if(response && response.result === "KO" && response.details === "auth_denied") {
						const notificationStore = useNotificationStore();
						notificationStore.showNotification("auth-denied-error");
					}
				});
			}
		},
		getStatsOrdersAggregatedDualPeriod(periodDates, callback = null) {
			let userStore = useUserStore();
			if(!userStore.currBusinessId) return;

			// Calculate elapsed time in current period
			const now = new Date();
			let currentPeriodStart, currentPeriodEnd, previousPeriodStart, previousPeriodEnd;

			if (periodDates.timeframeIndex === 2) { // Current month
				// Current period: 1st of current month to now
				currentPeriodStart = this.firstDayOfCurrentMonth();
				currentPeriodEnd = now;
				
				// Calculate elapsed time
				const elapsedTime = currentPeriodEnd.getTime() - currentPeriodStart.getTime();
				
				// Previous period: 1st of previous month to same elapsed time
				previousPeriodStart = this.firstDayOfLastMonth();
				previousPeriodEnd = new Date(previousPeriodStart.getTime() + elapsedTime);
			} else if (periodDates.timeframeIndex === 6) { // Current year
				// Current period: 1st of current year to now
				currentPeriodStart = this.firstDayOfCurrentYear();
				currentPeriodEnd = now;
				
				// Calculate elapsed time
				const elapsedTime = currentPeriodEnd.getTime() - currentPeriodStart.getTime();
				
				// Previous period: 1st of previous year to same elapsed time
				previousPeriodStart = this.firstDayOfLastYear();
				previousPeriodEnd = new Date(previousPeriodStart.getTime() + elapsedTime);
			} else if (periodDates.timeframeIndex === 7) { // Custom date range
				// Check if month grain would be used (range > 70 days for each period)
				// When month grain is used, we need separate calls to properly filter partial months
				const daysDiff = this.getDayDifference(periodDates.start, periodDates.end);

				if (daysDiff > 70) {
					// Month grain will be used - make dual API calls for proper partial month filtering
					currentPeriodStart = periodDates.start;
					currentPeriodEnd = periodDates.end;
					previousPeriodStart = periodDates.previousStart;
					previousPeriodEnd = periodDates.previousEnd;
				} else {
					// Day/hour grain - single call is fine
					this.getStatsOrdersAggregated(
						formatDateTimeWithSeconds(periodDates.previousStart),
						formatDateTimeWithSeconds(periodDates.end),
						callback
					);
					return;
				}
			} else {
				// Other preset timeframes - fallback to single call
				this.getStatsOrdersAggregated(
					formatDateTimeWithSeconds(periodDates.previousStart),
					formatDateTimeWithSeconds(periodDates.end),
					callback
				);
				return;
			}

			// Format dates for API
			const currentStartDB = formatDateTimeWithSeconds(currentPeriodStart);
			const currentEndDB = formatDateTimeWithSeconds(currentPeriodEnd);
			const previousStartDB = formatDateTimeWithSeconds(previousPeriodStart);
			const previousEndDB = formatDateTimeWithSeconds(previousPeriodEnd);

			// Make two separate API calls
			let currentData = null;
			let previousData = null;
			let completed = 0;

			const checkComplete = () => {
				completed++;
				if (completed === 2 && currentData && previousData) {
					// Merge the data
					this.ordersAggregated = {
						full: [...previousData.full, ...currentData.full],
						type: [...previousData.type, ...currentData.type],
						mode: [...previousData.mode, ...currentData.mode],
						staff: [...previousData.staff, ...currentData.staff],
						payment_user: [...(previousData.payment_user || []), ...(currentData.payment_user || [])],
						timeGrain: currentData.timeGrain, // Use current period's grain
						bucketColumn: currentData.bucketColumn,
						users: [...new Map([...previousData.users, ...currentData.users].map(u => [u.user_id, u])).values()]
					};
					callback && callback({ result: "OK" });
				}
			};

			// Call for current period
			API.init().analyticsOrdersAggregated(userStore.currBusinessId, currentStartDB, currentEndDB, userStore.manualCode, response => {
				if (response && response.result === "OK") {
					currentData = {
						full: this.normalizeAggregatedData(response.aggregated_data.full || [], response.bucket_column, currentStartDB, 'current'),
						type: this.normalizeAggregatedData(response.aggregated_data.type || [], response.bucket_column, currentStartDB, 'current'),
						mode: this.normalizeAggregatedData(response.aggregated_data.mode || [], response.bucket_column, currentStartDB, 'current'),
						staff: this.normalizeAggregatedData(response.aggregated_data.staff || [], response.bucket_column, currentStartDB, 'current'),
						payment_user: this.normalizeAggregatedData(response.aggregated_data.payment_user || [], response.bucket_column, currentStartDB, 'current'),
						timeGrain: response.time_grain,
						bucketColumn: response.bucket_column,
						users: response.users || []
					};
					checkComplete();
				} else if(response && response.result === "KO" && response.details === "auth_denied") {
					const notificationStore = useNotificationStore();
					notificationStore.showNotification("auth-denied-error");
				}
			});

			// Call for previous period
			API.init().analyticsOrdersAggregated(userStore.currBusinessId, previousStartDB, previousEndDB, userStore.manualCode, response => {
				if (response && response.result === "OK") {
					previousData = {
						full: this.normalizeAggregatedData(response.aggregated_data.full || [], response.bucket_column, previousStartDB, 'previous'),
						type: this.normalizeAggregatedData(response.aggregated_data.type || [], response.bucket_column, previousStartDB, 'previous'),
						mode: this.normalizeAggregatedData(response.aggregated_data.mode || [], response.bucket_column, previousStartDB, 'previous'),
						staff: this.normalizeAggregatedData(response.aggregated_data.staff || [], response.bucket_column, previousStartDB, 'previous'),
						payment_user: this.normalizeAggregatedData(response.aggregated_data.payment_user || [], response.bucket_column, previousStartDB, 'previous'),
						timeGrain: response.time_grain,
						bucketColumn: response.bucket_column,
						users: response.users || []
					};
					checkComplete();
				} else if(response && response.result === "KO" && response.details === "auth_denied") {
					const notificationStore = useNotificationStore();
					notificationStore.showNotification("auth-denied-error");
				}
			});
		},
		normalizeAggregatedData(data, bucketColumn = 'day_bucket', startDate = null, periodTag = null) {
			// Determine which dates need adjustment based on startDate
			// Note: When periodTag is provided, we skip month_bucket date adjustment because
			// the period tag already distinguishes data and the adjustment would break
			// getDatesIndex mapping (adjusted dates fall outside x-axis bucket boundaries)
			let datesToAdjust = {};
			if (startDate && bucketColumn === 'month_bucket' && !periodTag) {
				const startDateObj = getCompatibleDate(startDate);
				// If start date is not on the 1st, we need to adjust that month
				if (startDateObj.getDate() > 1) {
					// Create the key for the month that needs adjustment (YYYY-MM-01)
					const monthKey = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-01`;
					datesToAdjust[monthKey] = startDateObj.getDate(); // Store the day to adjust to
				}
			} else if (startDate && bucketColumn === 'hour_bucket') {
				const startDateObj = getCompatibleDate(startDate);
				// If start time is not at :00, we need to adjust that hour
				if (startDateObj.getMinutes() > 0) {
					// Create the key for the hour that needs adjustment (YYYY-MM-DD HH:00:00)
					const hourKey = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')} ${String(startDateObj.getHours()).padStart(2, '0')}:00:00`;
					datesToAdjust[hourKey] = startDateObj.getMinutes(); // Store the minutes to adjust to
				}
			}
			
			// Adjust dates from calendar day to business day
			// Redshift groups by calendar day, but UI expects business days
			// Business day runs from 3am to 3am, so calendar day data needs +1 day adjustment
			data.forEach((row) => {
				// Tag row with period identifier if provided
				if (periodTag) {
					row._period = periodTag;
				}

				// Skip if bucket column doesn't exist
				if (!row[bucketColumn]) {
					return;
				}
				
				// Normalize numeric fields to ensure they're numbers, not strings
				Object.keys(row).forEach(key => {
					if (key !== bucketColumn && row[key] !== null && row[key] !== undefined) {
						// Check if the field should be numeric (common numeric fields in analytics)
						const numericFields = ['units_sold', 'gross_sales', 'net_total', 'discounts_total', 
											  'order_count', 'events_count', 'visits_count', 'total', 
											  'vat_total', 'service_charge_total', 'tips_total'];
						
						if (numericFields.some(field => key.includes(field)) || 
							(!isNaN(row[key]) && typeof row[key] === 'string')) {
							row[key] = Number(row[key]);
						}
					}
				});
				
				if (bucketColumn === 'hour_bucket') {
					// For hour_bucket, convert from server UTC to local timezone
					const localDate = getLocalDateFromServer(row[bucketColumn]);
					// Add 1 hour to shift the data forward
					localDate.setHours(localDate.getHours() + 1);
					
					// Check if this hour needs adjustment
					if (datesToAdjust[row[bucketColumn]]) {
						localDate.setMinutes(datesToAdjust[row[bucketColumn]]);
					}
					
					// Store as a proper Date object to preserve local timezone
					row[bucketColumn] = localDate.toISOString();
				} else if (bucketColumn === 'day_bucket') {
					// For day bucket, parse the date and add 1 day for business day adjustment
					const date = getCompatibleDate(row[bucketColumn]);
					if (isNaN(date.getTime())) {
						console.error('Invalid date in normalizeAggregatedData:', row[bucketColumn]);
						return;
					}
					// Store back as YYYY-MM-DD format
					date.setDate(date.getDate() + 1);
					row[bucketColumn] = date.toISOString().split('T')[0];
				} else if (bucketColumn === 'month_bucket') {
					// For month bucket, keep the date as-is (1st of month)
					// No adjustment needed since we want to align with x-axis dates
					const date = getCompatibleDate(row[bucketColumn]);
					if (isNaN(date.getTime())) {
						console.error('Invalid date in normalizeAggregatedData:', row[bucketColumn]);
						return;
					}
					
					// Check if this month needs adjustment
					if (datesToAdjust[row[bucketColumn]]) {
						date.setDate(datesToAdjust[row[bucketColumn]]);
					}
					
					// Store back as YYYY-MM-DD format
					row[bucketColumn] = date.toISOString().split('T')[0];
				}
			});
			return data;
		},
		getWeather(startDate, endDate, callback = null) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().getWeather(userStore.currBusinessId, startDate, endDate, response => {
					if (response && response.result === "OK") {
						(async () => {
							this.weather = this.normalizeWeather(response.weather);
							callback && callback(response);
						})()
					} else if(response && response.result === "KO" && response.details === "auth_denied") {
						const notificationStore = useNotificationStore();
						notificationStore.showNotification("auth-denied-error");
					}
				});
			}
		},
		getOrdersReviews(startDate, endDate, page = 1, callback = null) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsOrdersReviews(userStore.currBusinessId, startDate, endDate, page, 5, response => {
					if (response && response.result === "OK") {
						(async () => {
							if(page === 1) {
								this.ordersReviews = [];
							}
							// Add new reviews to the existing array
							this.ordersReviews.push(...this.normalizeReviews(response.reviews));
							// Update pagination state
							this.ordersReviewsPagination = {
								page: response.pagination.page,
								hasMore: response.pagination.has_more,
								total: response.pagination.total
							};
							callback && callback(response);
						})()
					}
				});
			}
		},
		getStatsProductsAggregated(startDate, endDate, callback = null, isToday = false) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsProductsAggregated(userStore.currBusinessId, startDate, endDate, response => {
					if (response && response.result === "OK") {
						(async () => {
							if (isToday) {
								// Store only the essential data for 24-hour view
								this.productsAggregatedToday = {
									products: this.normalizeAggregatedData(response.products || [], response.bucket_column, startDate),
									categories: this.normalizeAggregatedData(response.categories || [], response.bucket_column, startDate),
									variations: this.normalizeAggregatedData(response.variations || [], response.bucket_column, startDate),
									vat: this.normalizeAggregatedData(response.vat || [], response.bucket_column, startDate),
									products_metadata: response.products_metadata || [],
									categories_metadata: response.categories_metadata || [],
									variations_metadata: response.variations_metadata || [],
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							} else {
								this.productsAggregated = {
									products: this.normalizeAggregatedData(response.products || [], response.bucket_column, startDate),
									categories: this.normalizeAggregatedData(response.categories || [], response.bucket_column, startDate),
									variations: this.normalizeAggregatedData(response.variations || [], response.bucket_column, startDate),
									vat: this.normalizeAggregatedData(response.vat || [], response.bucket_column, startDate),
									products_metadata: response.products_metadata || [],
									categories_metadata: response.categories_metadata || [],
									variations_metadata: response.variations_metadata || [],
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							}
							callback && callback(response);
						})()
					}
				});
			}
		},
		getStatsTablesAggregated(startDate, endDate, callback = null, isToday = false) {
			let userStore = useUserStore();
			if(userStore.currBusinessId) {
				API.init().analyticsTablesAggregated(userStore.currBusinessId, startDate, endDate, response => {
					if (response && response.result === "OK") {
						(async () => {
							if (isToday) {
								// Store only the essential data for 24-hour view
								this.tablesAggregatedToday = {
									tables_orders: this.normalizeAggregatedData(response.tables_orders || [], response.bucket_column, startDate),
									rooms_orders: this.normalizeAggregatedData(response.rooms_orders || [], response.bucket_column, startDate),
									tables_visits: this.normalizeAggregatedData(response.tables_visits || [], response.bucket_column, startDate),
									rooms_visits: this.normalizeAggregatedData(response.rooms_visits || [], response.bucket_column, startDate),
									tables_metadata: response.tables_metadata || [],
									rooms_metadata: response.rooms_metadata || [],
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							} else {
								this.tablesAggregated = {
									tables_orders: this.normalizeAggregatedData(response.tables_orders || [], response.bucket_column, startDate),
									rooms_orders: this.normalizeAggregatedData(response.rooms_orders || [], response.bucket_column, startDate),
									tables_visits: this.normalizeAggregatedData(response.tables_visits || [], response.bucket_column, startDate),
									rooms_visits: this.normalizeAggregatedData(response.rooms_visits || [], response.bucket_column, startDate),
									tables_metadata: response.tables_metadata || [],
									rooms_metadata: response.rooms_metadata || [],
									timeGrain: response.time_grain,
									bucketColumn: response.bucket_column
								};
							}
							callback && callback(response);
						})()
					}
				});
			}
		},
		normalizeWeather(weather) {
			weather.forEach(w=>{
				w.date = getCompatibleDateOnly(w.date);
				w.date.setHours(12, 0, 0, 0);
				w.temperatureMin = Number(w.temperature_min);
				w.temperatureMax = Number(w.temperature_max);
				w.precipitation = Number(w.precipitation);
			});
			return weather;
		},
		normalizeOrders(orders, ordersItems = null, ordersFeedbacks = null, ordersItemsProducts = null, tables = null, rooms = null, menuItems = null, menuItemListProducts = null, menuCategories = null, transactions = null, transactionsPos = null, payments = null, plugins = null, users = null, cancelReceipts = null, ordersCoupons = null) {

			let ordersObj = {};
			let tablesObj = {};
			let roomsObj = {};
			let menuItemsObj = {};
			let menuItemListProductsObj = {};
			let menuCategoriesObj = {};
			let transactionsObj = {};
			let transactionsPosObj = {};
			let paymentsObj = {};
			let pluginsObj = {};
			let usersObj = {};
			let ordersCouponsObj = {};

			if(tables) {
				tables.forEach(table=>{
					tablesObj[table.room_table_id] = table;
				});
			}
			if(rooms) {
				rooms.forEach(room=>{
					roomsObj[room.room_id] = room;
				});
			}
			if(menuItems) {
				menuItems.forEach(menuItem=>{
					menuItemsObj[menuItem.menu_item_id] = menuItem;
				});
			}
			if(menuItemListProducts) {
				menuItemListProducts.forEach(menuItemListProduct=>{
					menuItemListProductsObj[menuItemListProduct.menu_item_list_product_id] = menuItemListProduct;
				});
			}
			if(menuCategories) {
				menuCategories.forEach(menuCategory=>{
					menuCategory.menu_id = Number(menuCategory.menu_id)
					menuCategoriesObj[menuCategory.menu_category_id] = menuCategory;
				});
			}
			if(transactions) {
				transactions.forEach(transaction=>{
					transactionsObj[transaction.order_id] = transaction;
				});
			}
			if(transactionsPos) {
				transactionsPos.forEach(transactionPos=>{
					transactionsPosObj[transactionPos.pii] = transactionPos;
				});
			}
			if(plugins) {
				plugins.forEach(plugin=>{
					pluginsObj[plugin.plugin_id] = plugin;
				});
			}
			if(users) {
				users.forEach(user=>{
					user.station = user.station === 1;
					usersObj[user.user_id] = user;
				});
			}
			if(payments) {
				payments.forEach(payment=>{
					payment.amount = Number(payment.amount);
					payment.amountFormatted = useCurrencyStore().dbToActualValue(payment.amount);
					payment.payment_user_id = payment.payment_user_id ? Number(payment.payment_user_id) : null;
					payment.payment_user = null;
					if(payment.payment_user_id) {
						payment.payment_user = usersObj[payment.payment_user_id] || null;
					}
					payment.cancelReceipt = false;
					if(payment.epson_receipt_id) {
						if(cancelReceipts) {
							cancelReceipts.forEach(receiptId=>{
								if(receiptId === payment.epson_receipt_id) {
									payment.cancelReceipt = true;
								}
							});
						}
					}
					if(paymentsObj[payment.order_id]) {
						paymentsObj[payment.order_id].push(payment);
					} else {
						paymentsObj[payment.order_id] = [payment];
					}
					payment.date = getLocalDateFromServer(payment.date);
				});
			}
			if(ordersCoupons) {
				ordersCoupons.forEach(ordersCoupon=>{
					ordersCoupon.business_coupon_id = Number(ordersCoupon.business_coupon_id);
					ordersCoupon.value = Number(ordersCoupon.value);
					ordersCoupon.order_id = Number(ordersCoupon.order_id);

					if(ordersCouponsObj.hasOwnProperty(ordersCoupon.order_id)) {
						ordersCouponsObj[ordersCoupon.order_id].push(ordersCoupon);
					}
					else {
						ordersCouponsObj[ordersCoupon.order_id] = [ordersCoupon];
					}
				});
			}

			const userStore = useUserStore();

			orders.forEach(order=>{
				order.date = getLocalDateFromServer(order.date);
				order.total = Number(order.grand_total || 0);
				order.cover_charge_num = Number(order.cover_charge_num || 0);
				order.coverChargeTotal = (order.cover_charge_num*Number(order.cover_charge_value || 0));
				order.paid = order.paid === 1;
				order.feedback = null;
				order.staff_user_id = order.staff_user_id ? Number(order.staff_user_id) : null;
				order.staff_user = null;
				if(order.staff_user_id) {
					order.staff_user = usersObj[order.staff_user_id] || null;
				}
				order.payment_user_id = order.payment_user_id ? Number(order.payment_user_id) : null;
				order.payment_user = null;
				if(order.payment_user_id) {
					order.payment_user = usersObj[order.payment_user_id] || null;
				}
				ordersObj[order.order_id] = order;
				ordersObj[order.order_id].items = [];
				ordersObj[order.order_id].payments = paymentsObj[order.order_id] || [];
				ordersObj[order.order_id].table = null;
				ordersObj[order.order_id].room = null;
				ordersObj[order.order_id].transaction = null;
				ordersObj[order.order_id].plugin = null;
				ordersObj[order.order_id].coupons = ordersCouponsObj[order.order_id] || [];

				if(!!userStore.manualCode && order.manual_multi) {
					ordersObj[order.order_id].total += Number(order.manual_multi || 0);
				}

				if(plugins && order.plugin_id && pluginsObj.hasOwnProperty(order.plugin_id)) {
					ordersObj[order.order_id].plugin = pluginsObj[order.plugin_id];
				}

				if(tables && order.room_table_id && tablesObj.hasOwnProperty(order.room_table_id)) {
					ordersObj[order.order_id].table = tablesObj[order.room_table_id];
					if(rooms && ordersObj[order.order_id].table.room_id && roomsObj.hasOwnProperty(ordersObj[order.order_id].table.room_id)) {
						ordersObj[order.order_id].room = roomsObj[ordersObj[order.order_id].table.room_id];
					}
				}

				if(transactions && transactionsObj.hasOwnProperty(order.order_id)) {
					ordersObj[order.order_id].transaction = transactionsObj[order.order_id];
				} else if(transactionsPos && order.pii && transactionsPosObj.hasOwnProperty(order.pii)) {
					ordersObj[order.order_id].transaction = transactionsPosObj[order.pii];
				}

				order.totalFormatted = useCurrencyStore().dbToActualValue(order.total);

				ordersObj[order.order_id].cancelReceipt = false;
				if(order.epson_receipt_id && cancelReceipts) {
					cancelReceipts.forEach(receiptId=>{
						if(receiptId === order.epson_receipt_id) {
							ordersObj[order.order_id].cancelReceipt = true;
						}
					});
				}
			});
			const epsonStore = useEpsonStore();
			let vats = epsonStore.countryVats["IT"];
			if(userStore.currBusiness && epsonStore.countryVats.hasOwnProperty(userStore.currBusiness.country)) {
				vats = epsonStore.countryVats[userStore.currBusiness.country];
			}
			if(ordersItems) {
				let itemsObj = {};
				ordersItems.forEach(item=>{
					/*let subtotalScaling = 1.0;
					if(ordersObj[item.order_id].subtotal_delta !== 0 && ordersObj[item.order_id].total !== 0) {
						subtotalScaling = (ordersObj[item.order_id].subtotal_delta + ordersObj[item.order_id].total) / ordersObj[item.order_id].total;
					}*/

					item.products = [];
					item.quantity = Number(item.quantity);
					item.vat = item.vat !== null && !Number.isNaN(Number(item.vat)) ? Number(item.vat) : parseFloat(vats[0]);
					item.totalWithProductsInOrderFormatted = Number(useCurrencyStore().dbToActualValue(item.price)*item.quantity);
					item.totalWithProductsInOrderFormattedTaxable = item.totalWithProductsInOrderFormatted/(1+(item.vat/100));
					itemsObj[item.order_item_id] = item;
					itemsObj[item.order_item_id].vat = Number(item.vat);

					itemsObj[item.order_item_id].name = "";
					itemsObj[item.order_item_id].color = null;
					if(menuItems && item.item_id && menuItemsObj.hasOwnProperty(item.item_id)) {
						itemsObj[item.order_item_id].name = menuItemsObj[item.item_id].name;
						itemsObj[item.order_item_id].color = menuItemsObj[item.item_id].color && menuItemsObj[item.item_id].color.length === 6 ? "#"+menuItemsObj[item.item_id].color : null;
					}

					itemsObj[item.order_item_id].category_name = "";
					itemsObj[item.order_item_id].category_color = null;
					if(menuCategories && item.category_id && menuCategoriesObj.hasOwnProperty(item.category_id)) {
						itemsObj[item.order_item_id].menu_id = menuCategoriesObj[item.category_id].menu_id;
						itemsObj[item.order_item_id].category_name = menuCategoriesObj[item.category_id].name;
						itemsObj[item.order_item_id].additional_costs_shortcuts = menuCategoriesObj[item.category_id].additional_costs_shortcuts;
						itemsObj[item.order_item_id].category_color = menuCategoriesObj[item.category_id].color && menuCategoriesObj[item.category_id].color.length === 6 ? "#"+menuCategoriesObj[item.category_id].color : null;
					}

					ordersObj[item.order_id].items.push(item);
				});
				if(ordersItemsProducts) {
					ordersItemsProducts.forEach(product=>{
						if(itemsObj[product.order_item_id]) {
							/*let subtotalScaling = 1.0;
							if(ordersObj[product.order_id].subtotal_delta !== 0 && ordersObj[product.order_id].total !== 0) {
								subtotalScaling = (ordersObj[product.order_id].subtotal_delta + ordersObj[product.order_id].total) / ordersObj[product.order_id].total;
							}*/
							product.quantity = Number(product.quantity);
							product.totalQuantity = product.quantity * itemsObj[product.order_item_id].quantity;
							product.price = Number(product.price);

							let additionalTotal = (itemsObj[product.order_item_id].quantity * Number(useCurrencyStore().dbToActualValue(product.price)*product.quantity));
							itemsObj[product.order_item_id].totalWithProductsInOrderFormatted += additionalTotal;
							itemsObj[product.order_item_id].totalWithProductsInOrderFormattedTaxable += additionalTotal/(1+(itemsObj[product.order_item_id].vat/100));

							product.name = "";
							product.totalFormatted = 0;
							product.totalFormattedTaxable = 0;
							if(menuItemListProducts && product.menu_item_list_product_id && menuItemListProductsObj.hasOwnProperty(product.menu_item_list_product_id)) {
								product.name = menuItemListProductsObj[product.menu_item_list_product_id].product_name;
								product.menu_item_linked_id = menuItemListProductsObj[product.menu_item_list_product_id].menu_item_linked_id
								let productTotal = (itemsObj[product.order_item_id].quantity * useCurrencyStore().dbToActualValue(product.price)*product.quantity);
								product.totalFormatted = productTotal;
								product.totalFormattedTaxable = productTotal/(1+(itemsObj[product.order_item_id].vat/100));
							}
							itemsObj[product.order_item_id].products.push(product);
						}
					});
				}
			}
			if(ordersFeedbacks) {
				ordersFeedbacks.forEach(feedback=>{

					feedback.date = getLocalDateFromServer(feedback.date);
					feedback.feedback_id = Number(feedback.feedback_id);

					if(ordersObj.hasOwnProperty(feedback.order_id) && !!ordersObj[feedback.order_id].customer_token) {
						ordersObj[feedback.order_id].feedback = feedback;
					}
				});
			}
			return Object.values(ordersObj);
		},
		normalizeOrdersTerminals(ordersTerminal, ordersTransactions = null, terminalsNames = null) {
			let result = [];
			if(ordersTerminal) {
				let ordersTransactionsObj = {};
				if(ordersTransactions) {
					ordersTransactions.forEach(transaction=>{
						ordersTransactionsObj[transaction.pii] = transaction;
					});
				}
				const terminalsNamesObj = terminalsNames || {};
				ordersTerminal.forEach(orderTerminal=>{
					orderTerminal.terminal_name = terminalsNamesObj[orderTerminal.terminal_id] || null;
					orderTerminal.total = Number(orderTerminal.total);
					orderTerminal.totalFormatted = useCurrencyStore().dbToActualValue(orderTerminal.total);
					orderTerminal.date = getLocalDateFromServer(orderTerminal.date);
					orderTerminal.transaction = null;
					orderTerminal.refundAmount = 0;
					if(ordersTransactionsObj.hasOwnProperty(orderTerminal.pii)) {
						orderTerminal.transaction = ordersTransactionsObj[orderTerminal.pii];
						if(orderTerminal.transaction && orderTerminal.transaction.status) {
							if("refunded" === orderTerminal.transaction.status) {
								orderTerminal.total = 0;
								orderTerminal.totalFormatted = 0;
								orderTerminal.refundAmount = orderTerminal.totalFormatted;
							} else if("partially_refunded" === orderTerminal.transaction.status) {
								let refundedAmount = orderTerminal.transaction.refunded_amount ? Number(orderTerminal.transaction.refunded_amount) : 0;
								orderTerminal.total = orderTerminal.total - refundedAmount;
								orderTerminal.totalFormatted = (orderTerminal.totalFormatted - refundedAmount/100);
								orderTerminal.refundAmount = refundedAmount/100;
							}
						}
					}
					result.push(orderTerminal);
				});
			}
			return result;
		},
		normalizeViews(views, tables = null, rooms = null) {

			let tablesObj = {};
			let roomsObj = {};

			if(tables) {
				tables.forEach(table=>{
					tablesObj[table.room_table_id] = table;
				});
			}
			if(rooms) {
				rooms.forEach(room=>{
					roomsObj[room.room_id] = room;
				});
			}

			views.forEach(view=>{
				view.date = getLocalDateFromServer(view.ds);
				view.visits_count = view.vc ? Number(view.vc) : 0;
				view.users_count = view.uc ? Number(view.uc) : 0;
				view.browser_data = view.bd ? JSON.parse(view.bd) : null;
				view.device_data = view.dd ? JSON.parse(view.dd) : null;
				view.os_data = view.od ? JSON.parse(view.od) : null;
				view.menu_data = view.md ? JSON.parse(view.md) : null;
				view.menu_user_data = view.mud ? JSON.parse(view.mud) : null;
				view.table_data = view.td ? JSON.parse(view.td) : null;
				view.table_user_data = view.tud ? JSON.parse(view.tud) : null;
				view.room_data = view.rd ? JSON.parse(view.rd) : null;
				view.room_user_data = view.rud ? JSON.parse(view.rud) : null;
				delete view.ds;
				delete view.vc;
				delete view.uc;
				delete view.bd;
				delete view.dd;
				delete view.od;
				delete view.md;
				delete view.mud;
				delete view.td;
				delete view.tud;
				delete view.rd;
				delete view.rud;
				if(tables) {
					if(view.table_data) {
						Object.entries(view.table_data).forEach(([room_table_id, value]) => {
							if(tablesObj.hasOwnProperty(room_table_id)) {
								view.table_data[room_table_id] = {};
								view.table_data[room_table_id].value = value;
								view.table_data[room_table_id].table = tablesObj[room_table_id];
							}
						});
					}
					if(view.table_user_data) {
						Object.entries(view.table_user_data).forEach(([room_table_id, value]) => {
							if(tablesObj.hasOwnProperty(room_table_id)) {
								view.table_user_data[room_table_id] = {};
								view.table_user_data[room_table_id].value = value;
								view.table_user_data[room_table_id].table = tablesObj[room_table_id];
							}
						});
					}
				}
				if(rooms) {
					if(view.room_data) {
						Object.entries(view.room_data).forEach(([room_id, value]) => {
							if(roomsObj.hasOwnProperty(room_id)) {
								view.room_data[room_id] = {};
								view.room_data[room_id].value = value;
								view.room_data[room_id].room = roomsObj[room_id];
							}
						});
					}
					if(view.room_user_data) {
						Object.entries(view.room_user_data).forEach(([room_id, value]) => {
							if(roomsObj.hasOwnProperty(room_id)) {
								view.room_user_data[room_id] = {};
								view.room_user_data[room_id].value = value;
								view.room_user_data[room_id].room = roomsObj[room_id];
							}
						});
					}
				}
			});
			views.sort((a, b) => {
				return a.date - b.date;
			});
			return views;
		},
		normalizeEvents(events, menuItems = null) {

			let menuItemsObj = {};

			if(menuItems) {
				menuItems.forEach(item=>{
					menuItemsObj[item.menu_item_id] = item;
				});
			}

			events.forEach(event=>{
				event.date = getLocalDateFromServer(event.ds);
				event.translations_count = event.tc ? Number(event.tc) : 0;
				event.translations_data = event.td ? JSON.parse(event.td) : null;
				event.scroll_bottom = event.sb ? Number(event.sb) : 0;
				event.zoom_image = event.zi ? Number(event.zi) : 0;
				event.menu_clicks = event.mc ? Number(event.mc) : 0;
				event.category_clicks = event.cc ? Number(event.cc) : 0;
				event.item_clicks = event.ic ? Number(event.ic) : 0;
				event.add_to_cart_data = event.ac ? JSON.parse(event.ac) : null;
				event.remove_from_cart_data = event.rc ? JSON.parse(event.rc) : null;
				event.time_avg = event.ta ? Number(event.ta) : 0;
				delete event.ds;
				delete event.tc;
				delete event.td;
				delete event.sb;
				delete event.zi;
				delete event.mc;
				delete event.cc;
				delete event.ic;
				delete event.ac;
				delete event.rc;
				delete event.ta;
				if(menuItems) {
					if(event.add_to_cart_data) {
						Object.entries(event.add_to_cart_data).forEach(([menu_item_id, value]) => {
							if(menuItemsObj.hasOwnProperty(menu_item_id)) {
								event.add_to_cart_data[menu_item_id] = {};
								event.add_to_cart_data[menu_item_id].value = value;
								event.add_to_cart_data[menu_item_id].product = menuItemsObj[menu_item_id];
							}
						});
					}
					if(event.remove_from_cart_data) {
						Object.entries(event.remove_from_cart_data).forEach(([menu_item_id, value]) => {
							if(menuItemsObj.hasOwnProperty(menu_item_id)) {
								event.remove_from_cart_data[menu_item_id] = {};
								event.remove_from_cart_data[menu_item_id].value = value;
								event.remove_from_cart_data[menu_item_id].product = menuItemsObj[menu_item_id];
							}
						});
					}
				}
			});
			events.sort((a, b) => {
				return a.date - b.date;
			});
			return events;
		},
		normalizeReviews(reviews) {
			const currencyStore = useCurrencyStore();
			const normalized = reviews.map(review => {
				// Parse dates
				review.date = getLocalDateFromServer(review.date);
				
				// If review has an order, structure it properly for CommentCloud
				if (review.order_data) {
					review.order = {
						order_id: review.order_id,
						order_number_day: review.order_data.order_number_day,
						totalFormatted: currencyStore.dbToActualValue(review.order_data.total),
						date: getLocalDateFromServer(review.order_data.date_confirmed),
						feedback: {
							feedback_id: review.feedback_id,
							quality_rating: review.quality_rating,
							pricing_rating: review.pricing_rating,
							service_rating: review.service_rating,
							location_rating: review.location_rating,
							details: review.details,
							date: review.date
						}
					};
				}
				
				return review;
			});
			return normalized;
		},
		normalizeTables(orders, views, events, rooms_tables, rooms) {

			let languageStore = useLanguageStore();

			let tablesObj = {};
			orders.forEach(order=>{

				if(order.room_table_id) {

					if(tablesObj.hasOwnProperty(order.room_table_id)) {
						tablesObj[order.room_table_id].totalFormatted += useCurrencyStore().dbToActualValue(order.total);
						tablesObj[order.room_table_id].orders.push(order);
					} else {
						tablesObj[order.room_table_id] = {
							orders: [order],
							views: [],
							events: [],
							room: {},
							name: languageStore.getString("unknown"),
							totalFormatted: 0,
						}
					}
				}

			});

			views.forEach(view=>{
				if(view.room_table_id) {
					if(tablesObj.hasOwnProperty(view.room_table_id)) {
						tablesObj[view.room_table_id].views.push(view);
					} else {
						tablesObj[view.room_table_id] = {
							orders: [],
							views: [view],
							events: [],
							room: {},
							name: languageStore.getString("unknown"),
							totalFormatted: 0,
						}
					}
				}
			});

			events.forEach(event=>{
				if(event.room_table_id) {
					if(
						event.name === "time"
					) {
						if(tablesObj.hasOwnProperty(event.value)) {
							tablesObj[event.value].events.push(event);
						}
					}
				}
			});

			let roomsObj = {};
			rooms.forEach(room=>{
				roomsObj[room.room_id] = room;
			});

			rooms_tables.forEach(room_table=>{
				if(tablesObj.hasOwnProperty(room_table.room_table_id)) {
					tablesObj[room_table.room_table_id].name = room_table.table_name;
					if(roomsObj.hasOwnProperty(room_table.room_id)) {
						tablesObj[room_table.room_table_id].room = roomsObj[room_table.room_id];
					}
				}
			});

			let result = Object.values(tablesObj);
			result.sort((a, b) => {
				return b.totalFormatted - a.totalFormatted;
			});
			return result;

		},
		xaxisTimesAggregated(periodDates) {
			// This is a version of xaxisTimes that skips week grain for aggregated charts
			// It goes directly from day to month grain when days > 60

			let languageStore = useLanguageStore();

			let finalTimes = [];
			if (periodDates.timeframeIndex === 0) {
				for (let i = 0; i < 7; i++) {
					const date = new Date(periodDates.end);
					date.setDate(date.getDate() - 6 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {weekday: 'short'})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
					});
				}
			} else if (periodDates.timeframeIndex === 1) {
				for (let i = 0; i < 28; i++) {
					const date = new Date(periodDates.end);
					date.setDate(date.getDate() - 27 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: 'short', hour12: false})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
					});
				}
			} else if (periodDates.timeframeIndex === 6) {
				// Current year - special handling to generate proper monthly labels
				let startTime = periodDates.start;
				let pastDays = this.getDayDifference(startTime, periodDates.end);

				if (pastDays <= 2) {
					// Very early in the year - use hourly
					let values = this.getDatesEvery60MinutesTillNow(startTime, periodDates.end);
					values.forEach(time => {
						// Get previous hour for range display
						const prevHour = new Date(time);
						prevHour.setHours(prevHour.getHours() - 1);

						const prevHourStr = prevHour.toLocaleTimeString([languageStore.lang], {hour: '2-digit', minute: '2-digit', hour12: false});
						const currHourStr = time.toLocaleTimeString([languageStore.lang], {hour: '2-digit', minute: '2-digit', hour12: false});

						finalTimes.push({
							rawDate: time,
							label: `${currHourStr}`,
							label_full: `${prevHourStr} - ${currHourStr}`,
						});
					});
				} else if (pastDays <= 70) {
					// First ~2 months - use daily
					let values = this.getDatesEveryXDaysTillNow(startTime, 1, periodDates.end);
					values.forEach(time => {
						let dateToRead = new Date(time);
						dateToRead.setDate(dateToRead.getDate() - 1);
						finalTimes.push({
							rawDate: time,
							label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: 'numeric', month: 'short'})),
							label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
						});
					});
				} else {
					// More than 70 days - use monthly, but properly aligned to month boundaries
					const startDate = new Date(periodDates.start);
					const endDate = new Date(periodDates.end);
					const currentMonth = endDate.getMonth();
					const currentYear = startDate.getFullYear();
					
					// Generate one label per month from January to current month
					for (let month = 0; month <= currentMonth; month++) {
						const date = new Date(currentYear, month, 1);
						// Add day shift hours to align with business day
						const userStore = useUserStore();
						let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
						let timeShiftArr = timeShift.split(":");
						date.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
						
						finalTimes.push({
							rawDate: date,
							label: capitalizeFirstLetter(date.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
							label_full: capitalizeFirstLetter(date.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'long', year: 'numeric'}))
						});
					}
				}
			} else if (periodDates.timeframeIndex === 2 || periodDates.timeframeIndex === 7) {
				let startTime = periodDates.start;
				let pastDays = this.getDayDifference(startTime, periodDates.end);
				if (pastDays <= 2) {
					let values = this.getDatesEvery60MinutesTillNow(startTime, periodDates.end);
					values.forEach(time => {
						// Get previous hour for range display
						const prevHour = new Date(time);
						prevHour.setHours(prevHour.getHours() - 1);

						const prevHourStr = prevHour.toLocaleTimeString([languageStore.lang], {hour: '2-digit', minute: '2-digit', hour12: false});
						const currHourStr = time.toLocaleTimeString([languageStore.lang], {hour: '2-digit', minute: '2-digit', hour12: false});

						finalTimes.push({
							rawDate: time,
							label: `${currHourStr}`,
							label_full: `${prevHourStr} - ${currHourStr}`,
						});
					});
				} else if (pastDays <= 70) {
					// Extended day grain up to 70 days (matching backend logic)
					let values = this.getDatesEveryXDaysTillNow(startTime, 1, periodDates.end);
					values.forEach(time => {
						let dateToRead = new Date(time);
						dateToRead.setDate(dateToRead.getDate() - 1);
						finalTimes.push({
							rawDate: time,
							label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: 'numeric', month: 'short'})),
							label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
						});
					});
				} else {
					// Jump directly to month grain after 70 days
					// Generate x-axis aligned with actual calendar months (1st of each month)
					// This matches backend's date_trunc('month', ...) grouping
					const startDate = new Date(periodDates.start);
					const endDate = new Date(periodDates.end);
					const userStore = useUserStore();
					let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
					let timeShiftArr = timeShift.split(":");

					// Start from the first day of the start month
					let currentMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
					currentMonth.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);

					// Generate one label per month from start month to end month
					while (currentMonth <= endDate) {
						finalTimes.push({
							rawDate: new Date(currentMonth),
							label: capitalizeFirstLetter(currentMonth.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
							label_full: capitalizeFirstLetter(currentMonth.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'long', year: 'numeric'}))
						});
						// Move to next month
						currentMonth.setMonth(currentMonth.getMonth() + 1);
					}
				}
			} else if (periodDates.timeframeIndex === 3) {
				// For 3 months, use month grain
				let startTime = new Date(periodDates.end);
				let daysAgo = 90;
				startTime.setDate(startTime.getDate() - daysAgo);
				let values = this.getDatesEveryXDaysTillNow(startTime, 30, periodDates.end);
				values.forEach(time => {
					let timeToRead = new Date(time);
					timeToRead.setDate(timeToRead.getDate() - 1);
					let dayOfMonthPreviousMonth = new Date(timeToRead);
					dayOfMonthPreviousMonth.setDate(dayOfMonthPreviousMonth.getDate() - 30);
					finalTimes.push({
						rawDate: time,
						label: capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: dayOfMonthPreviousMonth.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
					});
				});
			} else if (periodDates.timeframeIndex === 4) {
				let startTime = new Date(periodDates.end);
				let daysAgo = 180;
				startTime.setDate(startTime.getDate() - daysAgo);
				let values = this.getDatesEveryXDaysTillNow(startTime, 30, periodDates.end);
				values.forEach(time => {
					let timeToRead = new Date(time);
					timeToRead.setDate(timeToRead.getDate() - 1);
					let dayOfMonthPreviousMonth = new Date(timeToRead);
					dayOfMonthPreviousMonth.setDate(dayOfMonthPreviousMonth.getDate() - 30);
					finalTimes.push({
						rawDate: time,
						label: capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: dayOfMonthPreviousMonth.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
					});
				});
			} else if (periodDates.timeframeIndex === 5) {
				for (let i = 0; i < 12; i++) {
					const date = new Date(periodDates.end);
					date.setMonth(date.getMonth() - 11 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'long'}))
					});
				}
			}
			return finalTimes;
		},
		xaxisTimes(periodDates) {

			let languageStore = useLanguageStore();

			let finalTimes = [];
			if (periodDates.timeframeIndex === 0) {
				for (let i = 0; i < 7; i++) {
					const date = new Date(periodDates.end);
					date.setDate(date.getDate() - 6 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {weekday: 'short'})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
					});
				}
			} else if (periodDates.timeframeIndex === 1) {
				for (let i = 0; i < 28; i++) {
					const date = new Date(periodDates.end);
					date.setDate(date.getDate() - 27 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: 'short', hour12: false})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
					});
				}
			} else if (periodDates.timeframeIndex === 2 || periodDates.timeframeIndex === 6 || periodDates.timeframeIndex === 7) {
				let startTime = periodDates.start;
				let pastDays = this.getDayDifference(startTime, periodDates.end);
				if (pastDays <= 2) {
					let values = this.getDatesEvery60MinutesTillNow(startTime, periodDates.end);
					values.forEach(time => {
						let label = capitalizeFirstLetter(time.toLocaleTimeString([languageStore.lang], {hour: '2-digit', minute: '2-digit', hour12: false}));
						finalTimes.push({
							rawDate: time,
							label: label,
							label_full: label,
						});
					});
				} else if (pastDays <= 31) {
					let values = this.getDatesEveryXDaysTillNow(startTime, 1, periodDates.end);
					values.forEach(time => {
						let dateToRead = new Date(time);
						dateToRead.setDate(dateToRead.getDate() - 1);
						finalTimes.push({
							rawDate: time,
							label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: 'numeric', month: 'short'})),
							label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', weekday: 'short', month: '2-digit'}))
						});
					});
				} else if (pastDays <= 98) {
					let values = this.getDatesEveryXDaysTillNow(startTime, 7, periodDates.end);
					values.forEach(time => {
						let timeToRead = new Date(time);
						timeToRead.setDate(timeToRead.getDate() - 1);
						let weekOfMonth = Math.floor((((timeToRead.getDate()-1) / 7) + 1));
						let dayOfMonthPreviousWeek = new Date(timeToRead);
						dayOfMonthPreviousWeek.setDate(dayOfMonthPreviousWeek.getDate() - 7);
						finalTimes.push({
							rawDate: time,
							label: weekOfMonth+languageStore.getString("week_letter")+"/"+capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
							label_full: dayOfMonthPreviousWeek.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
						});
					});
				} else {
					let values = this.getDatesEveryXDaysTillNow(startTime, 30, periodDates.end);
					values.forEach(time => {
						let timeToRead = new Date(time);
						timeToRead.setDate(timeToRead.getDate() - 1);
						let dayOfMonthPreviousMonth = new Date(timeToRead);
						dayOfMonthPreviousMonth.setDate(dayOfMonthPreviousMonth.getDate() - 30);
						finalTimes.push({
							rawDate: time,
							label: capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
							label_full: dayOfMonthPreviousMonth.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
						});
					});
				}
			} else if (periodDates.timeframeIndex === 3) {
				let startTime = new Date(periodDates.end);
				let daysAgo = 90;
				startTime.setDate(startTime.getDate() - daysAgo);
				let values = this.getDatesEveryXDaysTillNow(startTime, 7, periodDates.end);
				values.forEach(time => {
					let timeToRead = new Date(time);
					timeToRead.setDate(timeToRead.getDate() - 1);
					let weekOfMonth = Math.floor((((timeToRead.getDate()-1) / 7) + 1));
					let dayOfMonthPreviousWeek = new Date(timeToRead);
					dayOfMonthPreviousWeek.setDate(dayOfMonthPreviousWeek.getDate() - 7);
					finalTimes.push({
						rawDate: time,
						label: weekOfMonth+languageStore.getString("week_letter")+"/"+capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: dayOfMonthPreviousWeek.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
					});
				});
			} else if (periodDates.timeframeIndex === 4) {
				let startTime = new Date(periodDates.end);
				let daysAgo = 180;
				startTime.setDate(startTime.getDate() - daysAgo);
				let values = this.getDatesEveryXDaysTillNow(startTime, 30, periodDates.end);
				values.forEach(time => {
					let timeToRead = new Date(time);
					timeToRead.setDate(timeToRead.getDate() - 1);
					let dayOfMonthPreviousMonth = new Date(timeToRead);
					dayOfMonthPreviousMonth.setDate(dayOfMonthPreviousMonth.getDate() - 30);
					finalTimes.push({
						rawDate: time,
						label: capitalizeFirstLetter(timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: dayOfMonthPreviousMonth.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})+" - "+timeToRead.toLocaleDateString([languageStore.lang || 'en-US'], {day: '2-digit', month: '2-digit'})
					});
				});
			} else if (periodDates.timeframeIndex === 5) {
				for (let i = 0; i < 12; i++) {
					const date = new Date(periodDates.end);
					date.setMonth(date.getMonth() - 11 + i);
					let dateToRead = new Date(date);
					dateToRead.setDate(dateToRead.getDate() - 1);
					finalTimes.push({
						rawDate: date,
						label: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'short'})),
						label_full: capitalizeFirstLetter(dateToRead.toLocaleDateString([languageStore.lang || 'en-US'], {month: 'long'}))
					});
				}
			}
			return finalTimes;
		},
		getDatesIndex(periodDates, xaxisTimes, order, isPrevious = false) {
			//Assumes xaxisTimes is sorted
			if (xaxisTimes.length < 2) {
				return -1;
			}

			let comparingDate = isPrevious
				? this.getPreviousComparingDate(periodDates, order.date)
				: order.date;

			const comparingTime = comparingDate.getTime();

			// Iterate through buckets to find where comparingDate belongs
			// Each bucket represents a period ending at rawDate (inclusive of end, exclusive of start)
			// This approach correctly handles DST transitions where day lengths vary
			for (let i = 0; i < xaxisTimes.length; i++) {
				const currentTime = xaxisTimes[i].rawDate.getTime();
				const prevTime = i > 0 ? xaxisTimes[i - 1].rawDate.getTime() : Number.NEGATIVE_INFINITY;

				if (comparingTime > prevTime && comparingTime <= currentTime) {
					return i;
				}
			}

			return -1;
		},
		getPreviousComparingDate(periodDates, comparingDate) {
			let returnDate = new Date(comparingDate);
			if(periodDates.timeframeIndex === 0) {
				returnDate.setDate(returnDate.getDate() + 7);
			} else if(periodDates.timeframeIndex === 1) {
				returnDate.setDate(returnDate.getDate() + 28);
			} else if(periodDates.timeframeIndex === 2) {
				returnDate.setMonth(returnDate.getMonth() + 1);
			} else if(periodDates.timeframeIndex === 3) {
				returnDate.setDate(returnDate.getDate() + 90);
			} else if(periodDates.timeframeIndex === 4) {
				returnDate.setDate(returnDate.getDate() + 180);
			} else if(periodDates.timeframeIndex === 5) {
				returnDate.setMonth(returnDate.getMonth() + 12);
			} else if(periodDates.timeframeIndex === 6) {
				returnDate.setFullYear(returnDate.getFullYear() + 1);
			} else if(periodDates.timeframeIndex === 7) {
				// Custom dates - always shift by date difference to map previous to current period
				let dateDiff = periodDates.end.getTime() - periodDates.start.getTime();
				returnDate.setTime(returnDate.getTime() + dateDiff);
			}
			return returnDate;
		},
		numberOfDaysInTimeframePoint(periodDates) {
			let pastDays = this.getDayDifference(periodDates.start, periodDates.end);
			if(pastDays <= 2) {
				return 1;
			} else if(pastDays <= 70) { // Changed from 60 to 70 to match backend aggregation logic
				return 1;
			} else {
				return 30;
			}
		},
		firstDayOfCurrentMonth() {
			const userStore = useUserStore();
			let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
			let timeShiftArr = timeShift.split(":");
			const date = new Date();
			return new Date(date.getFullYear(), date.getMonth(), 1, Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
		},
		firstDayOfCurrentYear() {
			const userStore = useUserStore();
			let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
			let timeShiftArr = timeShift.split(":");
			const date = new Date();
			return new Date(date.getFullYear(), 0, 1, Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
		},
		firstDayOfLastMonth() {
			const date = this.firstDayOfCurrentMonth();
			date.setMonth(date.getMonth() - 1);
			return date;
		},
		firstDayOfLastYear() {
			const date = this.firstDayOfCurrentYear();
			date.setFullYear(date.getFullYear() - 1);
			return date;
		},
		getDatesEvery60MinutesTillNow(startDate, end = null) {
			let current = new Date();
			if(end) {
				current = new Date(end);
			}
			let dates = [];
			let i = 0;
			while (current > startDate && i <= 10000) {
				dates.unshift(new Date(current));
				current.setTime(current.getTime() - 60 * 60 * 1000);
				i++;
			}
			return dates;
		},
		getDatesEveryXDaysTillNow(startDate, days, end = null) {
			let current = new Date();
			if(end) {
				current = new Date(end);
			} else {
				const userStore = useUserStore();
				let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
				let timeShiftArr = timeShift.split(":");
				current.setDate(current.getDate() + 1);
				current.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
			}
			let dates = [];
			while (current > startDate) {
				dates.unshift(new Date(current));
				current.setDate(current.getDate() - days);
			}
			return dates;
		},
		getDayDifference(start, end) {
			let timeDiff = Math.abs(end.getTime() - start.getTime());
			return Math.ceil(timeDiff / (1000 * 3600 * 24));
		},
		getTargetStartDate(selectedTimeIndex) {
			let date = new Date();
			const userStore = useUserStore();
			let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
			let timeShiftArr = timeShift.split(":");
			date.setDate(date.getDate() + 1);
			date.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
			switch (selectedTimeIndex) {
				case 0: date.setDate(date.getDate() - 7); break;
				case 1: date.setDate(date.getDate() - 28); break;
				case 2: date = this.firstDayOfCurrentMonth(); break;
				case 3: date.setDate(date.getDate() - 90); break;
				case 4: date.setDate(date.getDate() - 180); break;
				case 5: date.setDate(date.getDate() - 365); break;
				case 6: date = this.firstDayOfCurrentYear(); break;
			}
			date.setMinutes(date.getMinutes() + 1);
			return date;
		},
		getTargetEndDate() {
			let date = new Date();
			const userStore = useUserStore();
			let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
			let timeShiftArr = timeShift.split(":");
			date.setDate(date.getDate() + 1);
			date.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
			return date;
		},
		getTargetPreviousStartDate(selectedTimeIndex) {
			let date = new Date(this.getTargetEndDate());
			switch (selectedTimeIndex) {
				case 0: date.setDate(date.getDate() - 7*2); break;
				case 1: date.setDate(date.getDate() - 28*2); break;
				case 2: date = this.firstDayOfLastMonth(); break;
				case 3: date.setDate(date.getDate() - 90*2); break;
				case 4: date.setDate(date.getDate() - 180*2); break;
				case 5: date.setDate(date.getDate() - 365*2); break;
				case 6: date = this.firstDayOfLastYear(); break;
			}
			date.setMinutes(date.getMinutes() + 1);
			return date;
		},
		getTargetPreviousEndDate(selectedTimeIndex) {
			let date = new Date(this.getTargetStartDate(selectedTimeIndex));
			let staticDaysIndexes = [0, 1, 3, 4, 5];
			if(staticDaysIndexes.includes(selectedTimeIndex)) {
				date.setMinutes(date.getMinutes() - 1);
			} else {
				date = this.getTargetEndDate();
				if(selectedTimeIndex === 2) {
					date.setMonth(date.getMonth() - 1);
				} else if(selectedTimeIndex === 6) {
					date.setFullYear(date.getFullYear() - 1);
				}
				const userStore = useUserStore();
				let timeShift = userStore.currBusiness && userStore.currBusiness.dayShift ? userStore.currBusiness.dayShift : "03:00:00";
				let timeShiftArr = timeShift.split(":");
				date.setHours(Number(timeShiftArr[0]) || 3, Number(timeShiftArr[1]) || 0, Number(timeShiftArr[2]) || 0);
			}
			return date;
		},
		getExecutedByFilterResult(filter, order) {
			let skip = false;

			if(filter.active) {
				let columnToCheck = filter.value.typeOfUserToFilterBy === 0 ? "staff_user_id" : "payment_user_id";
				let columnValue = filter.value.listOfUserToFilterBy[order[columnToCheck]];
				let userToFilterBy = filter.value.userToFilterBy ? columnValue : false;
				let onlineUser = false;
				if(filter.value.onlineClient) {
					onlineUser = order[columnToCheck] === null;
				}

				if(!userToFilterBy && !onlineUser) {
					skip = true;
				}
			}

			return skip;
		}
	},
});
